import { useEffect, useRef, useState } from 'react';
import { BookOpen, Sparkles } from 'lucide-react';
import { Button, Checkbox, Modal, Spinner } from '../../../school_panel/components/ui';
import { SchoolApi } from '../../../school_panel/api/client';
import { useAnnotateBooks } from '../../../school_panel/api/hooks';
import type { AnnotateBook, AnnotateResult, ImportedQuestion, QuestionPayload } from '../../../school_panel/api/types';
import { gradeLabel } from '../../../school_panel/lib/gradeLabel';

export const ANNOTATE_CHUNK = 10;

const errText = (err: unknown) => {
  const r = (err as { response?: { status?: number; data?: { detail?: string; message?: string } } })?.response;
  if (r?.status === 429) return r.data?.message || r.data?.detail || 'Your AI budget for this month is used up.';
  return r?.data?.detail || 'The AI could not finish. Try again.';
};

const gradeOf = (b: AnnotateBook) => (b.grades[0] ? gradeLabel(b.grades[0].name) : 'No grade');

function BookRow({ book, checked, disabled, onToggle }: { book: AnnotateBook; checked: boolean; disabled: boolean; onToggle: () => void }) {
  const off = disabled || !book.indexed;
  return (
    <li>
      <label className={`flex items-center gap-3 rounded-[12px] border px-3 py-2.5 ${checked ? 'border-navy bg-note' : 'border-line bg-surface'} ${off ? 'opacity-60' : 'cursor-pointer'}`}>
        <Checkbox size={20} checked={checked} disabled={off} onChange={onToggle} aria-label={book.title} />
        <BookOpen size={16} aria-hidden className="flex-none text-muted" />
        <span className="min-w-0 flex-1 truncate text-sm text-ink">{book.title}</span>
        {!book.indexed && <span className="text-xs text-muted">Not searchable yet</span>}
      </label>
    </li>
  );
}

/**
 * After the AI reads a sheet: pick the books, then for each question the AI finds the chapter,
 * writes the explanation and cites the page it used. Results arrive chunk by chunk (`onResults`).
 */
export default function AnnotateDialog({ examId, items, open, onResults, onClose }: {
  examId: number;
  items: ImportedQuestion[];
  open: boolean;
  onResults: (start: number, results: AnnotateResult[]) => void;
  onClose: () => void;
}) {
  const books = useAnnotateBooks(examId, open);
  const [picked, setPicked] = useState<Set<number> | null>(null);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const stop = useRef(false);

  // The worksheet grade's books are picked by default.
  useEffect(() => {
    if (books.data && picked === null) setPicked(new Set(books.data.filter((b) => b.for_exam_grade && b.indexed).map((b) => b.id)));
  }, [books.data, picked]);
  useEffect(() => { if (open) { setDone(0); setError(null); stop.current = false; } }, [open]);

  const list = books.data ?? [];
  const mine = list.filter((b) => b.for_exam_grade);
  const others = new Map<string, AnnotateBook[]>();
  for (const b of list.filter((x) => !x.for_exam_grade)) others.set(gradeOf(b), [...(others.get(gradeOf(b)) ?? []), b]);
  const chosen = picked ?? new Set<number>();
  const toggle = (id: number) => setPicked((p) => { const n = new Set(p ?? []); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const total = items.length;

  const run = async (from: number) => {
    setRunning(true); setError(null); stop.current = false;
    let at = from;
    try {
      while (at < total && !stop.current) {
        const chunk = items.slice(at, at + ANNOTATE_CHUNK).map((it) => it.question as QuestionPayload);
        const results = await SchoolApi.authoringAnnotate(examId, chunk, Array.from(chosen));
        onResults(at, results);
        at += chunk.length;
        setDone(at);
      }
      if (at >= total) onClose();
    } catch (err) {
      setError(errText(err));
    } finally {
      setRunning(false);
    }
  };

  const footer = running ? (
    <Button variant="secondary" onClick={() => { stop.current = true; }}>Stop after this batch</Button>
  ) : error ? (
    <>
      <Button variant="ghost" onClick={onClose}>Keep what's done</Button>
      <Button onClick={() => run(done)}>Try the rest again</Button>
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={onClose}>Not now</Button>
      <Button icon={<Sparkles size={16} aria-hidden />} disabled={books.isPending} onClick={() => run(0)}>
        Add to {total} {total === 1 ? 'question' : 'questions'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={open} onClose={() => { if (!running) onClose(); }} size="md" closeOnBackdrop={false} closeOnEsc={!running}
      eyebrow="AI import"
      title="Add chapters, explanations and references?"
      subtitle={`For each question the AI finds the chapter, writes an explanation and cites the page it used, from the books you pick. You can change them before adding.`}
      footer={footer}>
      {running || done > 0 ? (
        <div className="space-y-3" role="status">
          <div className="flex items-center gap-2 text-sm text-ink">
            {running && <Spinner size="sm" />}
            {done >= total ? `All ${total} questions done.`
              : running ? `Working on questions ${done + 1}–${Math.min(done + ANNOTATE_CHUNK, total)} of ${total}…`
                : `${done} of ${total} questions done.`}
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-2">
            <div className="h-full rounded-full bg-navy transition-all" style={{ width: `${Math.round((done / Math.max(1, total)) * 100)}%` }} />
          </div>
          {error && <p role="alert" className="rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{error}</p>}
        </div>
      ) : books.isPending ? (
        <div className="flex justify-center py-6"><Spinner /></div>
      ) : (
        <div className="space-y-4">
          {error && <p role="alert" className="rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{error}</p>}
          {list.length === 0 ? (
            <p className="rounded-[12px] bg-surface-2 px-3 py-2.5 text-sm text-muted">
              This subject has no books yet. The AI will pick chapters and write explanations from its own knowledge, without references.
            </p>
          ) : (
            <>
              <section aria-label="This grade's books">
                <h3 className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">This grade's books</h3>
                {mine.length ? (
                  <ul className="space-y-1.5">{mine.map((b) => <BookRow key={b.id} book={b} checked={chosen.has(b.id)} disabled={running} onToggle={() => toggle(b.id)} />)}</ul>
                ) : <p className="text-sm text-muted">None for this grade. Add another grade's book below.</p>}
              </section>
              {others.size > 0 && (
                <section aria-label="Other grades' books">
                  <h3 className="mb-1 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">Other grades</h3>
                  <p className="mb-2 text-xs text-muted">Add one when the worksheet covers more than one grade.</p>
                  <div className="space-y-3">
                    {Array.from(others, ([g, bs]) => (
                      <div key={g}>
                        <p className="mb-1 text-xs font-semibold text-ink">{g}</p>
                        <ul className="space-y-1.5">{bs.map((b) => <BookRow key={b.id} book={b} checked={chosen.has(b.id)} disabled={running} onToggle={() => toggle(b.id)} />)}</ul>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {chosen.size === 0 && <p className="text-xs text-muted">No book picked: explanations come from the AI's own knowledge, without references.</p>}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
